import { Search, Zap, Eye, Brain } from "lucide-react";
import { Eyebrow, SectionTitle, SectionSub } from "@/components/web/atoms";
import { Reveal } from "@/components/web/home/primitives";

/* Section 3 — Ask / Do / Watch / Remember. Four columns that frame the whole
   product: intelligence connected to action. "Watch" items that depend on
   integrations are honestly marked so we never over-claim. */
const COLUMNS = [
  {
    key: "ask", icon: Search, tone: "ai", title: "Ask", tagline: "Use AI to think.",
    items: ["AI Research", "Compare models", "Analyze files", "Ask team knowledge", "Brainstorm", "Summarize"],
  },
  {
    key: "do", icon: Zap, tone: "brand", title: "Do", tagline: "Turn intelligence into work.",
    items: ["Create tasks", "Draft messages", "Generate reports", "Create documents", "Prepare meetings", "Update connected systems"],
  },
  {
    key: "watch", icon: Eye, tone: "brand", title: "Watch", tagline: "Let TeamNest monitor work.",
    note: "Available as supported integrations are connected.",
    items: [
      { label: "Deadlines" }, { label: "Projects" }, { label: "Important messages" },
      { label: "Recurring workflows" }, { label: "Leads", soon: true }, { label: "Customer issues", soon: true },
    ],
  },
  {
    key: "remember", icon: Brain, tone: "ai", title: "Remember", tagline: "Keep what matters.",
    items: ["Personal memory", "Team knowledge", "Research", "Files", "Decisions", "Role Intelligence"],
  },
];

export default function AskDoWatchRemember() {
  return (
    <section
      className="relative py-20 md:py-28 px-5 border-t border-[var(--w-hairline)] bg-[var(--w-bg2)] scroll-mt-20"
      data-testid="home-ask-do-watch-remember"
    >
      <div className="max-w-6xl mx-auto">
        <Reveal className="max-w-3xl">
          <Eyebrow tone="ai" className="mb-4">Ask · Do · Watch · Remember</Eyebrow>
          <SectionTitle>AI should do more than answer questions.</SectionTitle>
          <SectionSub className="mt-5">TeamNest connects intelligence to action.</SectionSub>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {COLUMNS.map((col, i) => {
            const accent = col.tone === "ai" ? "var(--w-ai)" : "var(--w-brand)";
            const tint = col.tone === "ai" ? "var(--w-ai-tint)" : "var(--w-brand-tint)";
            return (
              <Reveal key={col.key} delay={i * 70}>
                <div
                  className="h-full rounded-[18px] border border-[var(--w-hairline)] bg-[var(--w-surface)] p-5"
                  data-testid={`adwr-${col.key}`}
                >
                  <span className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: tint }}>
                    <col.icon className="w-5 h-5" style={{ color: accent }} />
                  </span>
                  <div className="text-[18px] font-bold text-[var(--w-text)]">{col.title}</div>
                  <div className="text-[13px] text-[var(--w-text-dim)] mb-4">{col.tagline}</div>
                  <ul className="space-y-2">
                    {col.items.map((it) => {
                      const label = typeof it === "string" ? it : it.label;
                      const soon = typeof it === "object" && it.soon;
                      return (
                        <li key={label} className="flex items-center gap-2 text-[13.5px] text-[var(--w-text-dim)]">
                          <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: accent }} />
                          <span>{label}</span>
                          {soon && (
                            <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full border border-[var(--w-hairline)] text-[var(--w-text-mute)]">
                              Coming soon
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                  {col.note && (
                    <p className="mt-4 text-[11px] leading-4 text-[var(--w-text-mute)] italic">{col.note}</p>
                  )}
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
